import React, { useEffect, useState } from 'react';

import Gallery from './Gallery';
import SelectLanguage from '../components/SelectLanguage';
import { ApiBase_slashless, loadVideoGallery, saveVideoGallery } from '../utils/Network';

/** FileField URLs from the API are often relative; <video> must load from the media host. */
function videoFileUrl(path) {
    if (path == null || path === '') return null;
    if (/^https?:\/\//i.test(String(path))) return path;
    const base = String(ApiBase_slashless).replace(/\/$/, '');
    const p = String(path);
    return p.startsWith('/') ? `${base}${p}` : `${base}/${p}`;
}

function videoMimeType(path) {
    const p = String(path).toLowerCase();
    if (p.endsWith('.webm')) return 'video/webm';
    if (p.endsWith('.ogg') || p.endsWith('.ogv')) return 'video/ogg';
    if (p.endsWith('.mov')) return 'video/quicktime';
    if (p.endsWith('.mp4') || p.endsWith('.m4v')) return 'video/mp4';
    return undefined;
}

export default function VideoGalleryContent({
    id,
    embedInModal = false,
    onClose = null,
    onSaved = null,
}) {
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);
    const [title, setTitle] = useState('');
    const [tag, setTag] = useState('');
    const [file_path, setFilePath] = useState(null);
    const [error, setError] = useState(null);
    const [file, setFile] = useState(null);
    const [gallery_modal_is_open, setGalleryModalIsOpen] = useState(false);
    const [gallery_item, setGalleryItem] = useState(false);
    const [language_id, setLanguageId] = useState(null);
    const [image, setImage] = useState(false);

    useEffect(() => {
        setDone(false);
        setError(null);
        setFile(null);
        setGalleryItem(false);
        if (id) {
            loadVideoGallery(id).then((vg) => {
                setTitle(vg.title || '');
                setTag(vg.tag || '');
                setLanguageId(vg.language);
                setImage(vg.image);
                setFilePath(vg.file);
            });
        } else {
            setTitle('');
            setTag('');
            setLanguageId(null);
            setImage(false);
            setFilePath(null);
        }
    }, [id]);

    const onChange = (e) => {
        switch (e.target.name) {
            case 'title':
                setTitle(e.target.value);
                break;
            case 'tag':
                setTag(e.target.value);
                break;
            default:
                break;
        }
    };

    const handleFileChange = (e) => {
        const f = e.target.files && e.target.files[0];
        if (f) setFile(f);
    };

    const SaveChanges = () => {
        setError(null);

        if (!title) {
            setError('Please enter Title');
            return;
        }

        if (!language_id) {
            setError('Please select Language');
            return;
        }

        if (!id && !file) {
            setError('Please select a File');
            return;
        }

        setLoading(true);
        saveVideoGallery(
            id,
            title,
            language_id,
            gallery_item ? gallery_item.image : null,
            tag,
            file
        )
            .then((videogalleryitem) => {
                if (videogalleryitem && videogalleryitem.id) {
                    setDone('Changes saved successfully');
                    setFilePath(videogalleryitem.file);
                    if (embedInModal && onSaved) {
                        onSaved(videogalleryitem);
                    }
                    if (embedInModal && onClose) {
                        onClose();
                    }
                } else {
                    const msg =
                        (videogalleryitem && videogalleryitem.detail) ||
                        (videogalleryitem && videogalleryitem.message) ||
                        'Save failed.';
                    setError(typeof msg === 'string' ? msg : 'Save failed.');
                }
                setLoading(false);
            })
            .catch(() => {
                setLoading(false);
                setError('Save failed. Please try again.');
            });
    };

    const openGallery = () => {
        setGalleryModalIsOpen(true);
    };
    const closeGallery = () => {
        setGalleryModalIsOpen(false);
    };

    const setSelectedImageGallery = (item) => {
        setGalleryItem(item);
    };

    const handleValue = (value, name) => {
        if (name === 'language') {
            setLanguageId(value);
        }
    };

    const editorForm = (
        <section className={embedInModal ? 'content cms-videogallery-editor-embed' : 'content'}>
            <div className="row">
                <div className="col-md-8">
                    <div className={embedInModal ? 'cms-dark-box no-border' : 'box'}>
                        <div className="box-header">
                            Video gallery details
                        </div>
                        <div className={`box-body padding-10 ${embedInModal ? '' : 'bg-ddd'}`}>
                            <div className="form-group">
                                <input
                                    type="text"
                                    value={title}
                                    name="title"
                                    onChange={onChange}
                                    className="form-control post-title"
                                    placeholder="Title"
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-md-4">
                    <div className={embedInModal ? 'cms-dark-box no-border' : 'box bg-ddd'}>
                        <div className="box-header">Information</div>
                        <div className="box-body padding-10">
                            <div className="form-group">
                                <input
                                    type="text"
                                    value={tag}
                                    name="tag"
                                    onChange={onChange}
                                    className="form-control max-250"
                                    placeholder="Tag"
                                />
                            </div>

                            <div className="form-group ">
                                {file_path != null && (
                                    <video
                                        key={file_path}
                                        className="cms-videogallery-edit-player"
                                        controls
                                        playsInline
                                        preload="metadata"
                                        width="100%"
                                    >
                                        <source
                                            src={videoFileUrl(file_path)}
                                            type={videoMimeType(file_path)}
                                        />
                                    </video>
                                )}
                                <input
                                    type="file"
                                    name="file"
                                    accept="video/*,.mp4,.webm,.mov,.m4v"
                                    onChange={handleFileChange}
                                    className="form-control max-250 "
                                />
                            </div>
                            <div className="form-group">
                                <SelectLanguage
                                    width={250}
                                    callback={handleValue}
                                    value={language_id}
                                    border={'1'}
                                />
                            </div>
                            <div className="form-group ">
                                <button
                                    type="button"
                                    className="cms-videogallery-poster-btn"
                                    onClick={openGallery}
                                    title="Choose poster from image gallery"
                                >
                                    <img
                                        className="img upload-img mb-0"
                                        src={
                                            gallery_item
                                                ? gallery_item.image
                                                : image
                                                  ? image
                                                  : 'images/default.png'
                                        }
                                        alt=""
                                    />
                                </button>
                            </div>
                        </div>

                        <div className="box-footer">
                            <button
                                type="button"
                                className="btn btn-primary pull-right"
                                onClick={SaveChanges}
                            >
                                Save Changes
                            </button>
                            {embedInModal ? (
                                <button
                                    type="button"
                                    className="btn btn-default pull-left"
                                    onClick={onClose}
                                >
                                    Cancel
                                </button>
                            ) : (
                                <a href="/cms/videogallerylist" className="btn btn-default pull-left">
                                    Cancel
                                </a>
                            )}
                        </div>
                        {error && (
                            <div className="alert alert-danger mt-5">{error}</div>
                        )}
                        {loading && (
                            <div className="alert alert-info  mt-5">Please Wait...</div>
                        )}
                        {done && !embedInModal && (
                            <div className="alert alert-success  mt-5">{done}</div>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );

    return (
        <>
            <Gallery
                setSelectedGalleryItem={setSelectedImageGallery}
                isOpen={gallery_modal_is_open}
                key={gallery_modal_is_open}
                closeGallery={() => {
                    closeGallery();
                }}
            />

            {embedInModal ? (
                <div className="cms-videogallery-editor-modal-inner">{editorForm}</div>
            ) : (
                <div className="content-wrapper">
                    <section className="content-header">
                        <h3 className="box-title mb-10"></h3>
                        <ol className="breadcrumb">
                            <li>
                                <a href="/cms">
                                    <i className="fa fa-dashboard"></i> Home
                                </a>
                            </li>
                            <li className="active">Video Gallery Item</li>
                        </ol>
                    </section>
                    {editorForm}
                </div>
            )}
        </>
    );
}
